<script lang="ts">
  import { REG, ROMAN, STAGE_ACCENT, LAYER, LAYER_NOTE, SRC, CUR, THREAD, byId, type Entry, type RegId } from '../data/grimoire';
  import { buildSigil } from '../sigil';
  import { focusTrap } from '../util';
  import { store } from '../store.svelte';

  const e = $derived(store.openEntry);
  let closeBtn = $state<HTMLButtonElement>(null!);
  let sheetEl = $state<HTMLDivElement>(null!);
  let progress = $state(0);
  let copied = $state(false);
  let copyTimer: ReturnType<typeof setTimeout> | null = null;

  function onScroll() {
    if (!sheetEl) return;
    const max = sheetEl.scrollHeight - sheetEl.clientHeight;
    progress = max > 0 ? Math.min(1, sheetEl.scrollTop / max) : 0;
  }

  async function copyLink() {
    if (!e) return;
    const url = `${location.origin}${location.pathname}#${e.id}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Fallback for older browsers / insecure contexts.
      const ta = document.createElement('textarea');
      ta.value = url;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
      } catch {
        /* ignore */
      }
      ta.remove();
    }
    copied = true;
    if (copyTimer) clearTimeout(copyTimer);
    copyTimer = setTimeout(() => (copied = false), 1800);
  }

  let lastFocus: HTMLElement | null = $state(null);
  let prevOpenId: string | null = null;

  $effect(() => {
    const id = store.openId;
    if (id && id !== prevOpenId) {
      lastFocus = document.activeElement as HTMLElement;
      prevOpenId = id;
      requestAnimationFrame(() => closeBtn?.focus());
    } else if (!id && prevOpenId) {
      lastFocus?.focus?.();
      lastFocus = null;
      prevOpenId = null;
    }
  });

  const regOf = (e2: Entry) => REG[e2._ri ?? 0];
  const accent = $derived(e ? STAGE_ACCENT[e._reg as RegId] : 'var(--nigredo)');

  const layerField = $derived.by(() => {
    if (!e) return null;
    if (e._reg === 'reg-1' && LAYER[e.id]) {
      return { label: 'Слой', value: `${LAYER[e.id]} — ${LAYER_NOTE[LAYER[e.id]]}` };
    }
    return null;
  });

  const curField = $derived.by(() => {
    if (!e || !e.cur) return null;
    const c = CUR[e.cur];
    return { label: 'Струна', value: `${c.g} ${c.nm}`, color: c.cc };
  });

  const sources = $derived(e && SRC[e.id] ? SRC[e.id] : null);

  const siblings = $derived.by(() => {
    if (!e || !e.cur || !THREAD[e.cur]) return [];
    return THREAD[e.cur]
      .map((sid, i) => (sid && sid !== e.id ? { sid, reg: `reg-${i + 1}` as RegId } : null))
      .filter((x): x is { sid: string; reg: RegId } => !!x);
  });

  function close() {
    store.close();
  }

  const nav = $derived.by(() => {
    // re-run when navSeq (bumped on open) or filter state changes
    return store.navContext;
  });

  function go(dir: -1 | 1) {
    const n = store.navContext;
    if (!n) return;
    const target = dir === -1 ? n.prev : n.next;
    // replace:true → walking within the chapter stays on a single history
    // slot, so one back press exits the modal instead of undoing N steps.
    if (target) store.open(target, { replace: true });
  }

  // In-sheet keyboard: arrow-left/right walk within the chapter. Avoid
  // triggering when user is in a form field (rare here, but safe).
  function onSheetKey(ev: KeyboardEvent) {
    if (ev.key === 'Escape') {
      close();
      return;
    }
    const t = ev.target as HTMLElement;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
    if (ev.metaKey || ev.ctrlKey || ev.altKey) return;
    if (ev.key === 'ArrowLeft') {
      ev.preventDefault();
      go(-1);
    } else if (ev.key === 'ArrowRight') {
      ev.preventDefault();
      go(+1);
    }
  }
</script>

<svelte:window onkeydown={onSheetKey} />

<div class="scrim" class:open={!!e} onclick={close} role="presentation"></div>

{#if e}
  <div
    class="sheet open"
    bind:this={sheetEl}
    use:focusTrap
    role="dialog"
    aria-modal="true"
    aria-labelledby="sheetName"
    style:--accent={accent}
    onscroll={onScroll}
  >
    <div class="sheet__progress" style:transform={`scaleX(${progress})`}></div>
    <button class="sheet__x" bind:this={closeBtn} aria-label="Закрыть" onclick={close}>✕</button>

    {#if nav}
      <div class="sheet__nav" role="group" aria-label="Навигация по узлам">
        <button
          class="nav-btn"
          aria-label="Предыдущий узел"
          disabled={!nav.prev}
          onclick={() => go(-1)}>←</button
        >
        <span class="nav-pos">{nav.pos} / {nav.total}</span>
        <button
          class="nav-btn"
          aria-label="Следующий узел"
          disabled={!nav.next}
          onclick={() => go(+1)}>→</button
        >
      </div>
    {/if}
    <div class="sheet__top">
      <div>
        <div class="sheet__ey">Карта {ROMAN[e._reg as RegId]} · {regOf(e).stage}</div>
        <h3 class="sheet__name" id="sheetName">{e.nm}</h3>
      </div>
      <div class="sheet__glyph">{@html buildSigil(e)}</div>
    </div>

    <div class="sheet__body">
      {#if layerField}
        <div class="field">
          <div class="field__l">{layerField.label}</div>
          <div class="field__v">{layerField.value}</div>
        </div>
      {/if}
      {#if curField}
        <div class="field">
          <div class="field__l">{curField.label}</div>
          <div class="field__v" style:color={curField.color}>{curField.value}</div>
        </div>
      {/if}
      {#each e.fields || [] as f (f.l)}
        <div class="field">
          <div class="field__l">{f.l}</div>
          <div class="field__v">{@html f.v}</div>
        </div>
      {/each}
      {#if e.levels}
        <div class="field">
          <div class="field__l">Лестница развития</div>
          <div class="ladder">
            {#each e.levels as [lvl, desc] (lvl)}
              <div class="rung"><b>{lvl}</b><span>{desc}</span></div>
            {/each}
          </div>
        </div>
      {/if}
      {#if sources}
        <div class="field">
          <div class="field__l">Источники</div>
          <div class="field__v src">
            {#each sources as [title, href], i (i)}
              {#if i > 0} · {/if}
              {#if href}
                <a href={href} target="_blank" rel="noopener noreferrer">{title}</a>
              {:else}
                <span>{title}</span>
              {/if}
            {/each}
          </div>
        </div>
      {/if}
    </div>

    {#if siblings.length > 0}
      <div class="sheet__rel">
        <div class="field__l">Та же струна в других регистрах</div>
        <div class="chips">
          {#each siblings as sib (sib.sid)}
            <span
              class="chip"
              role="button"
              tabindex="0"
              onclick={() => store.open(sib.sid, { replace: true })}
              onkeydown={(ev) =>
                ev.key === 'Enter' || ev.key === ' '
                  ? (ev.preventDefault(), store.open(sib.sid, { replace: true }))
                  : null}
            >
              <span class="r">{ROMAN[sib.reg]}</span>{byId[sib.sid].nm}
            </span>
          {/each}
        </div>
      </div>
    {/if}

    <div class="sheet__foot">
      <button class="link-btn" class:copied onclick={copyLink} aria-label="Скопировать ссылку">
        {#if copied}
          <span class="link-btn__icon">✓</span> скопировано
        {:else}
          <span class="link-btn__icon">⌁</span> ссылка на узел
        {/if}
      </button>
    </div>
  </div>
{/if}
